<template>
    <card>

        <h3 class="mb-0" slot="header">{{ $t('common.issues') }}</h3>

        <div v-if="sync">
            
            <base-alert type="warning" class="mb-4">
                <i class="fas fa-thumbs-up mr-3"></i> {{ $t('issues.go-online-notice') }}
            </base-alert>

            <base-button outline type="primary" @click.prevent="goOnline" class="ml-1">
                {{ $t('sessions.go-online') }}
            </base-button>
        </div>

        <div v-else-if="hasIssues">
            
            <p class="mb-4">
                {{ $t('issues.send-issues-notice') }}
            </p>

            <base-button type="primary" :loading="sending" @click.prevent="sendIssues">
                <i class="fas fa-paper-plane mr-1"></i> {{ $t('issues.send-now') }}
            </base-button>
        </div>

        <div v-else>
            
            <p class="mb-4">
                {{ $t('issues.nothing-to-send') }}
            </p>
        </div>

        <portal to="modals">

            <sync-modal v-model="modals.offlineSync" :sync="syncData" :autoRemoveAfterSync="true" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

            <confirm-modal v-model="modals.confirmSend">
                <p>{{ $t('issues.send-now-confirm') }}</p>
            </confirm-modal>

        </portal>
    </card>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import SyncModal from '../../offline/SyncModal'

    export default {

        components: {
            SyncModal
        },

        props: {
            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            stats: {
                type: Object,
                required: true
            },

            sync: {
                type: Object,
                default: null
            },
        },

        data() {
            return {
                sending: false,

                // Offline.
                modals: new ModalsController(['offlineSync', 'offlineError', 'confirmSend']),
                offlineErrorMessage: null,
                syncData: {
                    endpoint: '/socrates/api/front/offline/logs',
                    params: {
                        registration_id: this.registration.id,
                        simulation_id: this.simulation.id,
                        force: true,
                    },
                },
            }
        },

        created() {
            this.modals.init(this, 'modals')
        },

        computed: {

            hasIssues() {
                if (this.stats.global.issues > 0) {
                    return true
                }
                for (const timeslot of Object.values(this.stats.timeslots)) {
                    if (timeslot.issues > 0) {
                        return true
                    }
                }
                return false
            }
        },

        methods: {
            
            sendIssues() {
                this.modals.open('confirmSend').then(resp => {
                    if (resp.confirmed) {
                        this.sending = true

                        // Pilot. Just a notification.
                        axios.post('/socrates/api/front/dashboard/data/logs/registrations/'+this.registration.id+'/simulations/'+this.simulation.id+'/issues')
                        .then(resp => {
                            this.sending = false
                            this.$notify({type: 'primary', message: this.$t('feedbacks.sent') })
                        }).catch(error => {
                            this.sending = false
                            if (error.response.status == 404) {
                                this.$notify({type: 'danger', message: this.$t('feedbacks.send-failed') })
                            } else if (error.response.status == 500 && error.response.data.message) {
                                this.$notify({type: 'danger', message: error.response.data.message })
                            } else {
                                this.$auth.handleErrorVm(error, this)
                            }
                        });
                    }
                })
            },

            // Offline.

            goOffline() {
                this.$loader.show()
                axios.post(this.syncData.endpoint, this.syncData.params)
                .then(resp => {
                    this.$loader.hide()
                    this.$emit('reload')
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            goOnline() {
                if (this.sync.color == 'danger')  {
                    this.syncSession()
                } else {
                    this.removeOfflineSession()
                }
            },

            syncSession() {
                this.modals.open('offlineSync')
            },

            syncDone() {
                this.removeOfflineSession()
            },

            removeOfflineSession() {
                this.$loader.show()
                axios.delete(this.syncData.endpoint, { data: this.syncData.params })
                .then(resp => {
                    this.$loader.hide()
                    this.$emit('reload')
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },
        }
    }
</script>

